import styles from '@/components/static/StudyMaterialsGuidePage/StudyMaterialsGuidePage.module.scss'
import React, { ReactElement } from 'react'
import { BaseGuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponent/BaseGuideComponent'
import { GuideComponent } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/GuideComponent.types'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import Tooltip from 'antd/lib/tooltip'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { PhilosophyOwl } from '@/components/static/images/owlBranding/philosophy'
import { InstructorBannerPhilosophy } from '@/components/static/images/owlBranding/instructorBannerPhilosophy'
import { UserExperienceBannerOne } from '@/components/static/images/owlBranding/userExperience1'
import { UserExperienceBannerTwo } from '@/components/static/images/owlBranding/userExperience2'
import { BaseGuideComponentNoImage } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/BaseGuideComponentNoImage/BaseGuideComponentNoImage'
import { BackToTop } from '@/components/static/BackToTop/BackToTop'
import { ClickableArrow } from '@/components/ClickableArrow/ClickableArrow'
import { StudyMaterialsGuideExternalLink } from '@/components/static/StudyMaterialsGuidePage/GuideComponents/StudyMaterialsGuideExternalLink/StudyMaterialsGuideExternalLink'
import { OwlHasIdeaWithBackground } from '@/components/static/images/owlBranding/owlHasIdeaWithBackground'
import { getAbbreviationName } from '@/components/static/StudyMaterialsGuidePage/utils'

export const PhilosophyGuidePage: React.FC<GuideComponent> = (
    props
): ReactElement => {
    return (
        <div className={styles.studyMaterialsGuidePageBackground}>
            <BackToTop isLoggedIn={props.isLoggedIn} />

            <div className={styles.studyMaterialsGuidePage}>
                <div className={styles.headerContainer}>
                    <div className={reusableCssClass.centerChildrenVertically}>
                        <Tooltip
                            title={'Return to Overview'}
                            placement={'right'}
                        >
                            <ClickableArrow
                                orientation={'left'}
                                linkDestination={
                                    UiLayoutConstant.STUDY_MATERIALS_GUIDE_PAGE
                                }
                            />
                        </Tooltip>
                    </div>
                </div>
                <BaseGuideComponentNoImage
                    text={
                        <>
                            <div className={styles.title}>PHILOSOPHY</div>
                            <div className={styles.text}>
                                The goal of&nbsp;
                                {getAbbreviationName(props.courseConfigType)} is
                                to make your studying process as efficient as
                                possible - i.e. provide you with Just Enough
                                Prep. What differentiates&nbsp;
                                {getAbbreviationName(props.courseConfigType)}
                                &nbsp; from our competitors is our emphasis on
                                the user experience. Our instructors have used
                                other common study materials including both MATE
                                and TIA, and found that while the content
                                coverage was adequate, the format that the
                                content was presented in made studying both more
                                difficult and unpleasant than it needed to be.
                                Below is a list of features we strive to offer:
                            </div>
                        </>
                    }
                />

                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Incredible User Experience
                            </div>
                            <div>
                                As unbelievable as it may sound, our goal is to
                                make studying FUN. We believe this is possible
                                by:
                                <ul>
                                    <li>
                                        Automating the boring stuff - e.g.
                                        organizing notecard decks / practice
                                        quizzes, keeping track of problematic
                                        notecards, remembering to carry around
                                        physical study materials
                                    </li>
                                    <li>
                                        Watching yourself grow - using our
                                        interactive dashboards, it is both quick
                                        and easy to watch your progress over
                                        time, not to mention find neglected
                                        areas of the syllabus where more time
                                        should be spent. We also have an
                                        automated timer that keeps track of how
                                        much time you have studied per day and
                                        in total to help you meet your goals.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    }
                    nonBullet={
                        <div style={{ margin: '0 auto' }}>
                            <UserExperienceBannerOne />
                            <UserExperienceBannerTwo />
                        </div>
                    }
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>External Support</div>
                            <div>
                                Spending hundreds of hours studying for an exam,
                                especially an upper level exam where you may be
                                the only one at your company sitting, can be a
                                lonely experience. This is why we have the
                                Contact Instructor button on every page when
                                logged in, so you can request help from your
                                instructor about anything, be it related to the
                                content or about studying in general, and get
                                responses sent directly to your email.
                            </div>
                        </div>
                    }
                    nonBullet={<InstructorBannerPhilosophy />}
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'right'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Comprehensive Content
                            </div>
                            <div>
                                All testable material on the syllabus should be
                                covered in the course materials. This includes
                                different study formats, such as comprehensive
                                outlines, notecards, and practice problems.
                                After learning all the material included in the
                                &nbsp;
                                {getAbbreviationName(props.courseConfigType)}
                                &nbsp;course materials, you should expect a
                                minimal number of surprises come exam day.
                            </div>
                        </div>
                    }
                    nonBullet={<PhilosophyOwl />}
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
                <BaseGuideComponent
                    bulletSide={'left'}
                    bullets={
                        <div className={styles.bulletSection}>
                            <div className={styles.title}>
                                Active vs. Passive Learning
                            </div>
                            <div>
                                <ul>
                                    <li>
                                        <StudyMaterialsGuideExternalLink
                                            href={
                                                'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7383800/'
                                            }
                                            text={'Studies have shown'}
                                        />{' '}
                                        that active learning is more effective
                                        than passive learning at improving both
                                        retention and performance on
                                        examinations.
                                    </li>
                                    <li>
                                        The&nbsp;
                                        {getAbbreviationName(
                                            props.courseConfigType
                                        )}
                                        &nbsp;platform strongly embraces this
                                        concept. While watching videos (passive
                                        learning) may feel comfortable and easy,
                                        it&apos;s not a worthwhile use of time
                                        compared to actively attempting
                                        notecards and filling in gaps in
                                        knowledge by searching through outlines
                                        for specific information (active
                                        learning). You should be actively
                                        chasing that &quot;slightly
                                        uncomfortable&quot; feeling if you want
                                        to be using your study time optimally!
                                    </li>
                                    <li>
                                        What is active vs. passive learning?
                                        Read{' '}
                                        <StudyMaterialsGuideExternalLink
                                            href={
                                                'https://academicsupport.jhu.edu/resources/study-aids/active-versus-passive-learning/'
                                            }
                                            text={'here'}
                                        />{' '}
                                        for a more detailed description.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    }
                    nonBullet={<OwlHasIdeaWithBackground />}
                    shouldStackImagesAndBullets={
                        props.shouldStackImagesAndBullets
                    }
                />
            </div>
        </div>
    )
}
